import { useState, useEffect } from 'react';
import { ShieldCheck, ShieldOff, Loader as Loader2, Copy, CircleCheck as CheckCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export default function MfaSettingsSection() {
  const { refreshMfaStatus } = useAuth();
  const [hasFactor, setHasFactor] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    checkFactors();
  }, []);

  async function checkFactors() {
    setLoading(true);
    const { data } = await supabase.auth.mfa.listFactors();
    const verified = data?.totp?.some((f) => f.status === 'verified') ?? false;
    setHasFactor(verified);
    setLoading(false);
  }

  async function startEnroll() {
    setEnrolling(true);
    setError('');
    const { data, error: enrollError } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'X Manage Authenticator',
    });

    if (enrollError) {
      setError(enrollError.message);
      setEnrolling(false);
      return;
    }

    if (data) {
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFactorId(data.id);
    }
  }

  async function handleVerify() {
    setError('');
    setLoading(true);

    const { data: challengeData, error: challengeError } =
      await supabase.auth.mfa.challenge({ factorId });

    if (challengeError) {
      setError(challengeError.message);
      setLoading(false);
      return;
    }

    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code,
    });

    if (verifyError) {
      setError(verifyError.message);
      setCode('');
      setLoading(false);
      return;
    }

    setEnrolling(false);
    setQrCode('');
    setSecret('');
    setCode('');
    await checkFactors();
    await refreshMfaStatus();
    setLoading(false);
  }

  async function handleUnenroll() {
    if (!window.confirm('Disable two-factor authentication? You will only need your password to log in.')) return;

    setLoading(true);
    const { data } = await supabase.auth.mfa.listFactors();
    const verifiedFactors = data?.totp?.filter((f) => f.status === 'verified') || [];

    for (const factor of verifiedFactors) {
      await supabase.auth.mfa.unenroll({ factorId: factor.id });
    }

    await checkFactors();
    await refreshMfaStatus();
  }

  function handleCopy() {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading && hasFactor === null) {
    return (
      <div className="flex items-center gap-3 py-2.5 px-3">
        <Loader2 size={16} className="animate-spin text-white" />
        <span className="text-sm text-white">Checking 2FA status...</span>
      </div>
    );
  }

  if (enrolling && qrCode) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={16} className="text-teal-400" />
          <p className="text-sm font-semibold text-white">Set Up Two-Factor Authentication</p>
        </div>

        {error && (
          <div className="p-2.5 bg-red-500/10 border border-red-200 rounded-xl">
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}

        <div className="flex justify-center">
          <div className="bg-white p-2 rounded-xl border border-white/10">
            <img src={qrCode} alt="2FA QR Code" className="w-40 h-40" />
          </div>
        </div>

        <div>
          <p className="text-[11px] text-white mb-1.5">Manual entry code:</p>
          <div className="flex items-center gap-2 glass-inner border border-white/10 rounded-xl px-3 py-2">
            <code className="flex-1 text-xs text-teal-400 font-mono break-all select-all">{secret}</code>
            <button onClick={handleCopy} className="p-1 text-white hover:text-white shrink-0">
              {copied ? <CheckCircle size={14} className="text-teal-500" /> : <Copy size={14} />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-white mb-1.5">Verification Code</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            className="w-full px-4 py-2.5 bg-white/10 border border-white/10 text-white rounded-xl text-sm text-center tracking-[0.3em] font-mono focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
          />
        </div>

        <div className="flex gap-2.5">
          <button
            onClick={() => { setEnrolling(false); setQrCode(''); setSecret(''); setCode(''); setError(''); }}
            className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleVerify}
            disabled={loading || code.length < 6}
            className="flex-1 px-4 py-2.5 text-sm glass-button-active text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : 'Verify & Enable'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={hasFactor ? handleUnenroll : startEnroll}
      className="w-full flex items-center gap-3 py-2.5 glass-hover rounded-xl px-3"
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${hasFactor ? 'bg-teal-500/10' : 'bg-white/10'}`}>
        {hasFactor ? <ShieldCheck size={15} className="text-teal-400" /> : <ShieldOff size={15} className="text-white" />}
      </div>
      <div className="flex-1 text-left">
        <p className="text-sm font-semibold text-white">Two-Factor Authentication</p>
        <p className="text-[11px] text-white">
          {hasFactor ? 'Enabled - tap to disable' : 'Disabled - tap to set up'}
        </p>
      </div>
      <div className={`px-2 py-1 rounded-lg text-[10px] font-semibold ${hasFactor ? 'bg-teal-500/10 text-teal-400' : 'bg-white/10 text-white'}`}>
        {hasFactor ? 'ON' : 'OFF'}
      </div>
    </button>
  );
}
